<template>
    <div class="thumbnail__details">
        <div class="thumbnail__info-box">
            <span class="thumbnail__title">{{ entity.title }}</span>
            <div class="thumbnail__description -v-html">
                <span class="-v-html" v-html="truncateDescription"></span>
            </div>
            <span class="thumbnail__categories" v-if="!isCompletedView && displayedCategories !== ''">
                <span v-html="$t('General.categories')"></span>
                <span class="thumbnail__categories-names">
                    {{ displayedCategories }}
                    <template v-if="entityCategories.length > 5">...</template>
                </span>
            </span>
            <template v-if="itemType === 'course'">
                <span class="thumbnail__level">
                    <span v-html="$t('General.level')"></span>
                    <span class="thumbnail__level-number" v-html="$t(`Select.skill_level_${entity.level}`)"></span>
                </span>
                <span class="thumbnail__badges" v-if="shouldShowCourseCertificates">
                    <span v-html="$t('General.authorization')"></span>
                    <TotemCertificationBadge
                        v-for="(cert, index) in entity.certifiedBy"
                        :name="cert.label"
                        size="small"
                        :key="entity.id + 'certs-index' + index"
                    />
                </span>
            </template>
            <template v-if="itemType === 'collection' || itemType === 'learningpath'">
                <span class="thumbnail__level">
                    <span v-html="$t('General.level')"></span>
                    <template v-for="(level, index) in entity.levels"  >
                        <span :key="`level-${index}`" class="thumbnail__level-number" v-html="$t(`Select.skill_level_${level}`)"></span>
                        <span :key="index" v-if="index !== entity.levels.length - 1">,</span>
                    </template>
                </span>
                <span class="thumbnail__badges" v-if="shouldShowCollectionOrLPCertificates">
                    <span v-html="$t('General.authorization')"></span>
                    <TotemCertificationBadge
                        v-for="(cert, index) in entity.authorizationPoints"
                        :name="cert.authorizationName"
                        size="small"
                        :key="entity.id + 'certs-index' + index"
                    />
                </span>
            </template>
            <span v-if="entity.isTestAvailable" class="thumbnail__coursetest">
                <span class="icon-file-check"></span>
                <span v-html="$t('General.has_coursetest')"></span>
            </span>
            <span class="thumbnail__badges" v-if="entity.durations && entity.durations.length > 0">
                <span v-html="$t('General.authorization')"></span>
                <TotemAuthorizationHours :durations="entity.durations" />
            </span>
        </div>
    </div>
</template>

<script>
    import truncate from 'components/shared/mixins/truncate'

    export default {
        mixins: [ truncate ],
        props: {
            isCompletedView: {
                type: Boolean,
                default: false
            },
            entity: {
                type: Object,
                required: true
            },
            itemType: {
                type: String,
                default: 'course',
                required: true
            }
        },
        data () {
            return {
            }
        },
        computed: {
            shouldShowCertificates () {
                return this.$totemLang !== 'se'
            },
            shouldShowCourseCertificates () {
                return this.entity.certifiedBy?.length > 0 && this.$totemLang !== 'se'
            },
            shouldShowCollectionOrLPCertificates () {
                return this.entity.authorizationPoints?.length > 0 && this.$totemLang !== 'se'
            },
            entityCategories () {
                return this.entity.categoriesWithAncestors.map(
                    categoryWithAncestors => categoryWithAncestors.map(
                        ({ label }) => label
                    ).join(' > ')
                )
            },
            descLength () {
                if (this.$totemLang === 'no' && this.entity.durations && this.entity.durations.length > 0) {
                    return this.isMobileView ? 50 : 100
                }
                if (this.$totemLang === 'no' && this.entity.authorizationPoints && this.entity.authorizationPoints.length > 0) {
                    return this.isMobileView ? 50 : 300
                }
                if (this.entity.durations && this.entity.durations.length > 0) {
                    return this.isMobileView ? 100 : 300
                }
                return this.isMobileView ? 150 : 350
            },
            truncateDescription () {
                if (!this.entity.description) return ''
                if (this.entity.description.length < this.descLength) {
                    return this.entity.description
                } else {
                    return this.entity.description.substring(0, this.descLength) + '...'
                }
            },
            displayedCategories () {
                return this.entityCategories.slice(0, 5).map(category => category).join(', ')
            }
        }
    }
</script>

<style lang="scss" scoped>
    .thumbnail {
        @include course_list_item;

        cursor: auto;

        [class*="desktop"] & {
            margin: 0;
        }

        &.-hover {
            max-height: 400px;
        }

        &__info-box {
            display: flex;
            flex-direction: column;
            padding: 15px 10px;
        }

        &__title {
            font-family: PPAgrandir-Medium, sans-serif;
            font-size: 1.6rem;
            margin: 0 0 20px;
        }

        &__description {
            font-family: PlusJakartaSans-Regular, sans-serif;
            font-size: 1.4rem;
            margin: 0 0 20px;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 8;
            overflow: hidden;
            text-overflow: ellipsis;

            [class*="mobile"] & {
                margin: 0;
            }

            [class*="mobile_md"] & {
                -webkit-line-clamp: 3;
            }
        }

        &__categories {
            font-size: 1.4rem;
            padding: 0 0 5px;
            word-break: break-word;
            font-family: PlusJakartaSans-Bold, sans-serif;
        }

        &__categories-names {
            font-family: PlusJakartaSans-Regular, sans-serif;
        }

        &__level {
            font-family: PlusJakartaSans-Bold, sans-serif;
            font-size: 1.4rem;
            padding: 0 0 5px;
        }

        &__coursetest {
            font-size: 1.4rem;
            padding: 0 0 15px;
            display: flex;
            align-items: center;

            .icon-file-check {
                margin: 0 3px 0 0;
            }
        }

        &__level-number {
            font-family: PlusJakartaSans-Regular, sans-serif;
            margin: 0 0 0 5px;
        }

        &__badges {
            font-family: PlusJakartaSans-Bold, sans-serif;
            font-size: 1.4rem;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 6px;
            margin: 0 0 5px;
        }
    }
</style>
